import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import * as moment from "moment";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import Permission from "Components/Common/Permission";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { normalize } from "common/lib/normalize";
import { getSettings, updateSetting } from "common/services/settings.service";
import "./CronTasks.css";

type CronTaskSettings = {
  euSanctionsListImport: boolean;
  contractorDuplicateScan: boolean;
  ukSanctionsListImport: boolean;
  usSanctionsListImport: boolean;
  unDesignatedListImport: boolean;
  ungmListImport: boolean;
  performanceRatingsUpdate: boolean;
  performanceRatingsUpdateNotifications: boolean;
  userExpiryReminder: boolean;
};

type CronTaskKey = keyof CronTaskSettings;

const DEFAULT_CRON_TASK_SETTINGS: CronTaskSettings = {
  euSanctionsListImport: true,
  contractorDuplicateScan: true,
  ukSanctionsListImport: true,
  usSanctionsListImport: true,
  unDesignatedListImport: true,
  ungmListImport: true,
  performanceRatingsUpdate: true,
  performanceRatingsUpdateNotifications: true,
  userExpiryReminder: true,
};

const CRON_TASK_FIELDS: Array<{
  key: CronTaskKey;
  label: string;
  description: string;
}> = [
  {
    key: "euSanctionsListImport",
    label: "EU Sanctions List Import",
    description: "Controls the scheduled import for the EU sanctions list.",
  },
  {
    key: "contractorDuplicateScan",
    label: "Contractor Duplicate Scan",
    description: "Controls automated scans for duplicate contractor records.",
  },
  {
    key: "ukSanctionsListImport",
    label: "UK Sanctions List Import",
    description: "Controls the scheduled import for the UK sanctions list.",
  },
  {
    key: "usSanctionsListImport",
    label: "US Sanctions List Import",
    description: "Controls the scheduled import for the US sanctions list.",
  },
  {
    key: "unDesignatedListImport",
    label: "UN Designated List Import",
    description: "Controls the scheduled import for the UN designated list.",
  },
  {
    key: "ungmListImport",
    label: "UNGM List Import",
    description: "Controls the scheduled import for the UNGM debarred list.",
  },
  {
    key: "performanceRatingsUpdate",
    label: "Performance Ratings Update",
    description: "Controls scheduled performance rating refresh jobs.",
  },
  {
    key: "performanceRatingsUpdateNotifications",
    label: "Performance Ratings Update Notifications",
    description:
      "Controls notifications triggered by performance rating updates.",
  },
  {
    key: "userExpiryReminder",
    label: "User Expiry Reminder",
    description: "Controls reminder notifications for expiring user accounts.",
  },
];

const CronTasks = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Cron Tasks");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingId, setSettingId] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState<CronTaskSettings>(
    DEFAULT_CRON_TASK_SETTINGS
  );
  const [initialValues, setInitialValues] = useState<CronTaskSettings>(
    DEFAULT_CRON_TASK_SETTINGS
  );

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [initialValues, values]
  );

  const fetchCronTaskSettings = async () => {
    try {
      setLoading(true);

      const response: any = await getSettings({
        filters: {
          key: {
            $eq: "cron-tasks",
          },
        },
        pagination: {
          page: 1,
          pageSize: 1,
        },
      });

      const record = normalize(response.data)?.[0];

      if (!record) {
        setNotFound(true);
        setSettingId(null);
        return;
      }

      const nextValues = {
        ...DEFAULT_CRON_TASK_SETTINGS,
        ...(record.value || {}),
      };

      setNotFound(false);
      setSettingId(record.id);
      setDescription(record.description || "");
      setUpdatedAt(record.updatedAt || null);
      setValues(nextValues);
      setInitialValues(nextValues);
    } catch (error) {
      toast.error("Failed to load cron task settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCronTaskSettings();
  }, []);

  const handleToggle = (key: CronTaskKey) => {
    setValues((currentValues) => ({
      ...currentValues,
      [key]: !currentValues[key],
    }));
  };

  const handleSave = async () => {
    if (!settingId) {
      return;
    }

    try {
      setSaving(true);

      const response: any = await updateSetting({
        id: settingId,
        data: {
          value: values,
        },
      });

      const updatedRecord = response?.data ? normalize(response.data) : null;

      setInitialValues(values);
      setUpdatedAt(updatedRecord?.updatedAt || new Date().toISOString());
      toast.success("Cron task settings updated successfully");
    } catch (error) {
      toast.error("Failed to update cron task settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Cron Tasks"
            pageTitles={[
              { title: "Dashboard", link: "/dashboard" },
              { title: "Settings", link: "/settings/cron-tasks" },
            ]}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title mb-1">Cron Tasks</h5>
                  <p className="mb-0 text-muted">
                    Enable or disable scheduled background jobs from one place.
                  </p>
                </Col>
                <Col md="auto" className="ms-auto">
                  <Permission resource="setting" action="update">
                    <Button
                      color="primary"
                      onClick={handleSave}
                      disabled={!settingId || !isDirty || saving || loading}
                    >
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Permission>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  {notFound ? (
                    <Alert color="warning" className="mb-4">
                      The `cron-tasks` setting could not be found in the API
                      response.
                    </Alert>
                  ) : null}

                  {description ? (
                    <Alert color="light" className="border mb-4">
                      {description}
                    </Alert>
                  ) : null}

                  <div className="vstack gap-3">
                    {CRON_TASK_FIELDS.map((field) => {
                      const inputId = `cron-task-${field.key}`;

                      return (
                        <div
                          key={field.key}
                          className="border rounded p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3"
                        >
                          <div>
                            <h6 className="mb-1">{field.label}</h6>
                            <p className="mb-0 text-muted">
                              {field.description}
                            </p>
                          </div>

                          <div className="cron-task-switch-control">
                            <Label
                              className="cron-task-switch-status fw-medium mb-0"
                              htmlFor={inputId}
                            >
                              {values[field.key] ? "Enabled" : "Disabled"}
                            </Label>
                            <button
                              id={inputId}
                              type="button"
                              className={`cron-task-switch-button ${
                                values[field.key] ? "is-enabled" : "is-disabled"
                              }`}
                              role="switch"
                              aria-checked={values[field.key]}
                              aria-label={field.label}
                              disabled={!settingId || saving}
                              onClick={() => handleToggle(field.key)}
                            >
                              <span className="cron-task-switch-thumb" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {!notFound && updatedAt ? (
                    <p className="text-muted mb-0 mt-4">
                      Last updated{" "}
                      {moment(new Date(updatedAt)).format("DD MMM Y [at] HH:mm")}
                    </p>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CronTasks;
